import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  updateProfile as updateAuthProfile,
  type User as FirebaseUser,
} from "firebase/auth";
import { doc, getDoc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { auth, db } from "@/firebase/app";
import {
  activeContextFirestoreData,
  isMembershipCurrentlyActive,
  needsOnboarding,
  parseUser,
  resolvedLeagueID,
  userFirestoreData,
  type SBotActiveContext,
  type SBotUser,
} from "@/models/user";
import { syncCurrentUserMemberSummaries } from "@/services/account";
import { preparePushNotificationsForSignOut } from "@/features/notifications/PushNotificationProvider";
import { writeWebMCPBridgeEnabled } from "@/features/ai/webmcp/webmcpPreference";
import { workspaceOptions as loadWorkspaceOptions, type SBotWorkspaceOption } from "./workspaceOptions";
import {
  pinnedMembershipIsActive,
  readUntetheredWorkspaceContext,
  writeUntetheredWorkspaceContext,
} from "./untetheredWorkspace";

// Port of Services/AuthSession.swift. Remembered-account/keychain features are
// intentionally omitted on web — browsers provide credential management.

const BOOTSTRAP_SBOT_ADMIN_EMAILS = new Set([
  "tom@bcclive.com",
  "dave@bcclive.com",
  "melinda@bcclive.com",
  "marcus@bcclive.com",
  "dylan@bcclive.com",
  "ryan@bcclive.com",
]);

const ADMIN_MODE_STORAGE_PREFIX = "sbot.adminModeEnabled.";

export interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  currentUser: SBotUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isResolvingSession: boolean;
  isSBotLabsAdmin: boolean;
  isSBotAdminModeEnabled: boolean;
  workspaceOptions: SBotWorkspaceOption[];
  authError: string | null;
  profileMaintenanceWarning: string | null;
  signIn(email: string, password: string): Promise<boolean>;
  createAccount(email: string, password: string, firstName: string, lastName: string): Promise<boolean>;
  signInWithGoogle(): Promise<boolean>;
  resetPassword(email: string): Promise<void>;
  signOut(): Promise<void>;
  switchActiveWorkspace(optionID: string): Promise<boolean>;
  isWorkspaceUntethered: boolean;
  setWorkspaceUntethered(enabled: boolean): void;
  setSBotAdminModeEnabled(enabled: boolean): void;
  clearAuthError(): void;
  refreshWorkspaceOptions(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function trimmedEmail(email: string): string {
  return email.trim().toLowerCase();
}

function authErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "code" in error) {
    const code = String((error as { code: unknown }).code);
    switch (code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
      case "auth/user-not-found":
        return "Email or password is incorrect.";
      case "auth/email-already-in-use":
        return "An account already exists for this email.";
      case "auth/weak-password":
        return "Choose a stronger password (at least 6 characters).";
      case "auth/invalid-email":
        return "Enter a valid email address.";
      case "auth/popup-closed-by-user":
        return "Google sign-in was cancelled.";
      case "auth/too-many-requests":
        return "Too many attempts. Try again in a few minutes.";
      default:
        break;
    }
  }
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [currentUser, setCurrentUser] = useState<SBotUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResolvingSession, setIsResolvingSession] = useState(true);
  const [isSBotLabsAdmin, setIsSBotLabsAdmin] = useState(false);
  const [isSBotAdminModeEnabled, setIsSBotAdminModeEnabledState] = useState(false);
  const [workspaceOptions, setWorkspaceOptions] = useState<SBotWorkspaceOption[]>([]);
  const [authError, setAuthError] = useState<string | null>(null);
  const [profileMaintenanceWarning, setProfileMaintenanceWarning] = useState<string | null>(null);
  const [isWorkspaceUntethered, setIsWorkspaceUntetheredState] = useState(false);

  const profileUnsubscribeRef = useRef<(() => void) | null>(null);
  const currentUserRef = useRef<SBotUser | null>(null);
  currentUserRef.current = currentUser;
  // Per-window workspace pin. The onSnapshot closure below is long-lived, so the
  // pin must be read through a ref rather than state.
  const untetheredContextRef = useRef<SBotActiveContext | null>(null);
  // Last un-overlaid remote profile, so disabling untether can revert without a refetch.
  const remoteProfileRef = useRef<SBotUser | null>(null);

  const stopProfileListener = useCallback(() => {
    profileUnsubscribeRef.current?.();
    profileUnsubscribeRef.current = null;
  }, []);

  const clearSessionState = useCallback(() => {
    stopProfileListener();
    setFirebaseUser(null);
    setCurrentUser(null);
    setIsSBotLabsAdmin(false);
    setIsSBotAdminModeEnabledState(false);
    setWorkspaceOptions([]);
    setProfileMaintenanceWarning(null);
    setIsResolvingSession(false);
    // In-memory only: auth state can flip to null transiently during redirect
    // sign-in, and the sessionStorage pin is uid-scoped, so it stays put here.
    untetheredContextRef.current = null;
    remoteProfileRef.current = null;
    setIsWorkspaceUntetheredState(false);
  }, [stopProfileListener]);

  /**
   * Overlays the per-window workspace pin onto a remote profile before it
   * reaches consumers. If the pinned membership was revoked, drops the pin and
   * retethers so guards never see a stale context.
   */
  const applyWorkspacePin = useCallback((profile: SBotUser): SBotUser => {
    const pin = untetheredContextRef.current;
    if (!pin) return profile;
    if (!pinnedMembershipIsActive(profile, pin)) {
      untetheredContextRef.current = null;
      writeUntetheredWorkspaceContext(profile.id, null);
      setIsWorkspaceUntetheredState(false);
      return profile;
    }
    return { ...profile, activeContext: pin };
  }, []);

  const refreshWorkspaceOptionsFor = useCallback(async (user: SBotUser | null) => {
    setWorkspaceOptions(await loadWorkspaceOptions(user));
  }, []);

  const refreshWorkspaceOptions = useCallback(async () => {
    await refreshWorkspaceOptionsFor(currentUserRef.current);
  }, [refreshWorkspaceOptionsFor]);

  /** Mirrors AuthSession.refreshCustomClaims: bootstrap emails → claim → sbotAdmins doc. */
  const resolveSBotLabsAdmin = useCallback(async (user: FirebaseUser): Promise<boolean> => {
    const normalizedEmail = user.email?.trim().toLowerCase();
    if (normalizedEmail && BOOTSTRAP_SBOT_ADMIN_EMAILS.has(normalizedEmail)) {
      return true;
    }
    try {
      const tokenResult = await user.getIdTokenResult(false);
      if (tokenResult.claims.sbotLabsAdmin === true) {
        return true;
      }
      if (normalizedEmail) {
        const snapshot = await getDoc(doc(db, "sbotAdmins", normalizedEmail));
        return snapshot.exists();
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  /** Mirrors AuthSession.loadOrCreateProfile. */
  const loadOrCreateProfile = useCallback(
    async (user: FirebaseUser, pendingNames?: { firstName: string; lastName: string }): Promise<SBotUser> => {
      const document = doc(db, "users", user.uid);
      const snapshot = await getDoc(document);
      const now = new Date();

      const existing = snapshot.exists() ? parseUser(user.uid, snapshot.data()) : null;
      if (existing) {
        try {
          await updateDoc(document, { lastLoginAt: now, updatedAt: now });
        } catch (error) {
          setProfileMaintenanceWarning(`Profile loaded, but login metadata could not be updated. ${authErrorMessage(error)}`);
        }
        return { ...existing, lastLoginAt: now, updatedAt: now };
      }

      const profile: SBotUser = {
        id: user.uid,
        email: (user.email ?? "").toLowerCase(),
        displayName: user.displayName ?? undefined,
        photoURL: user.photoURL ?? undefined,
        firstName: pendingNames?.firstName.trim() ?? "",
        lastName: pendingNames?.lastName.trim() ?? "",
        phoneNumber: user.phoneNumber ?? undefined,
        socialProfiles: [],
        favoriteSports: [],
        favoriteTeams: [],
        roles: [],
        memberships: [],
        onboardingStatus: "notStarted",
        createdAt: now,
        updatedAt: now,
        lastLoginAt: now,
      };
      await setDoc(document, userFirestoreData(profile), { merge: true });
      return profile;
    },
    [],
  );

  /** Mirrors AuthSession.syncMemberSummaries: only call when league-linked memberships exist. */
  const syncMemberSummaries = useCallback(async (profile: SBotUser) => {
    const hasLeagueMemberships = profile.memberships.some((m) => isMembershipCurrentlyActive(m) && resolvedLeagueID(m));
    if (!hasLeagueMemberships) {
      setProfileMaintenanceWarning(null);
      return;
    }
    try {
      await syncCurrentUserMemberSummaries();
      setProfileMaintenanceWarning(null);
    } catch (error) {
      setProfileMaintenanceWarning(`Profile loaded, but league member summaries could not be synced. ${authErrorMessage(error)}`);
    }
  }, []);

  const refreshAdminMode = useCallback((userID: string, isAdmin: boolean) => {
    if (!isAdmin) {
      setIsSBotAdminModeEnabledState(false);
      return;
    }
    setIsSBotAdminModeEnabledState(localStorage.getItem(ADMIN_MODE_STORAGE_PREFIX + userID) === "true");
  }, []);

  const observeProfile = useCallback(
    (userID: string) => {
      stopProfileListener();
      profileUnsubscribeRef.current = onSnapshot(
        doc(db, "users", userID),
        (snapshot) => {
          if (!snapshot.exists()) return;
          const profile = parseUser(userID, snapshot.data());
          if (!profile) return;
          remoteProfileRef.current = profile;
          const effective = applyWorkspacePin(profile);
          setCurrentUser(effective);
          void refreshWorkspaceOptionsFor(effective);
        },
        (error) => {
          if (auth.currentUser?.uid === userID) {
            setAuthError(authErrorMessage(error));
          }
        },
      );
    },
    [applyWorkspacePin, refreshWorkspaceOptionsFor, stopProfileListener],
  );

  /** Mirrors AuthSession.handleAuthStateChange / activateSignedInUser. */
  const activateUser = useCallback(
    async (user: FirebaseUser, pendingNames?: { firstName: string; lastName: string }) => {
      const isAdmin = await resolveSBotLabsAdmin(user);
      setIsSBotLabsAdmin(isAdmin);
      refreshAdminMode(user.uid, isAdmin);
      const profile = await loadOrCreateProfile(user, pendingNames);
      untetheredContextRef.current = readUntetheredWorkspaceContext(user.uid);
      remoteProfileRef.current = profile;
      const effective = applyWorkspacePin(profile);
      // After applyWorkspacePin — it clears the ref when the pin went stale.
      setIsWorkspaceUntetheredState(untetheredContextRef.current !== null);
      setCurrentUser(effective);
      await syncMemberSummaries(effective);
      await refreshWorkspaceOptionsFor(effective);
      observeProfile(user.uid);
    },
    [applyWorkspacePin, loadOrCreateProfile, observeProfile, refreshAdminMode, refreshWorkspaceOptionsFor, resolveSBotLabsAdmin, syncMemberSummaries],
  );

  const pendingNamesRef = useRef<{ firstName: string; lastName: string } | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      void (async () => {
        setIsResolvingSession(true);
        setFirebaseUser(user);
        setAuthError(null);

        if (!user) {
          clearSessionState();
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        try {
          const pendingNames = pendingNamesRef.current;
          pendingNamesRef.current = undefined;
          await activateUser(user, pendingNames);
        } catch (error) {
          setAuthError(authErrorMessage(error));
        } finally {
          setIsLoading(false);
          setIsResolvingSession(false);
        }
      })();
    });

    return () => {
      unsubscribe();
      stopProfileListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAuthOperation = useCallback(async (operation: () => Promise<void>): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);
    try {
      await operation();
      return true;
    } catch (error) {
      setAuthError(authErrorMessage(error));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signIn = useCallback(
    (email: string, password: string) =>
      runAuthOperation(async () => {
        await signInWithEmailAndPassword(auth, trimmedEmail(email), password);
      }),
    [runAuthOperation],
  );

  const createAccount = useCallback(
    (email: string, password: string, firstName: string, lastName: string) =>
      runAuthOperation(async () => {
        pendingNamesRef.current = { firstName, lastName };
        const result = await createUserWithEmailAndPassword(auth, trimmedEmail(email), password);
        const displayName = `${firstName} ${lastName}`.trim();
        if (displayName) {
          await updateAuthProfile(result.user, { displayName });
        }
      }),
    [runAuthOperation],
  );

  const signInWithGoogle = useCallback(
    () =>
      runAuthOperation(async () => {
        const provider = new GoogleAuthProvider();
        try {
          await signInWithPopup(auth, provider);
        } catch (error) {
          const code = error && typeof error === "object" && "code" in error ? String((error as { code: unknown }).code) : "";
          if (code === "auth/popup-blocked" || code === "auth/operation-not-supported-in-this-environment") {
            await signInWithRedirect(auth, provider);
            return;
          }
          throw error;
        }
      }),
    [runAuthOperation],
  );

  const resetPassword = useCallback(
    async (email: string) => {
      await runAuthOperation(async () => {
        await sendPasswordResetEmail(auth, trimmedEmail(email));
        setAuthError("Password reset email sent.");
      });
    },
    [runAuthOperation],
  );

  const signOut = useCallback(async () => {
    try {
      // Untethering is a transient working mode; explicit sign-out ends it.
      const uid = auth.currentUser?.uid;
      if (uid) {
        writeUntetheredWorkspaceContext(uid, null);
        // Same for the external-agent bridge: it is a per-window working mode, not an account setting.
        writeWebMCPBridgeEnabled(uid, false);
      }
      await preparePushNotificationsForSignOut();
      await firebaseSignOut(auth);
      clearSessionState();
    } catch (error) {
      setAuthError(authErrorMessage(error));
    }
  }, [clearSessionState]);

  /** Mirrors AuthSession.switchActiveWorkspace: replace activeContext so stale optional scope fields are removed. */
  const switchActiveWorkspace = useCallback(
    async (optionID: string): Promise<boolean> => {
      const profile = currentUserRef.current;
      if (!profile) {
        setAuthError("User profile is not loaded.");
        return false;
      }
      const option = workspaceOptions.find((o) => o.membershipID === optionID);
      if (!option) {
        setAuthError("Workspace is no longer available.");
        return false;
      }
      const membershipExists = profile.memberships.some(
        (m) =>
          isMembershipCurrentlyActive(m) &&
          m.targetType === option.targetType &&
          m.targetID === option.targetID &&
          m.role === option.role,
      );
      if (!membershipExists) {
        setAuthError("Workspace is no longer active.");
        return false;
      }
      if (untetheredContextRef.current) {
        // Untethered: pin locally only. No Firestore write, so other windows
        // don't follow and the account-level default is untouched.
        untetheredContextRef.current = option.activeContext;
        writeUntetheredWorkspaceContext(profile.id, option.activeContext);
        setCurrentUser({ ...profile, activeContext: option.activeContext, updatedAt: new Date() });
        return true;
      }
      return runAuthOperation(async () => {
        const updatedAt = new Date();
        await updateDoc(doc(db, "users", profile.id), {
          activeContext: activeContextFirestoreData(option.activeContext),
          updatedAt,
        });
        setCurrentUser({ ...profile, activeContext: option.activeContext, updatedAt });
      });
    },
    [runAuthOperation, workspaceOptions],
  );

  const setWorkspaceUntethered = useCallback((enabled: boolean) => {
    const profile = currentUserRef.current;
    if (!profile) return;
    if (enabled) {
      // Pin only an explicit selection; the UI disables the toggle otherwise.
      const pin = profile.activeContext;
      if (!pin) return;
      untetheredContextRef.current = pin;
      writeUntetheredWorkspaceContext(profile.id, pin);
      setIsWorkspaceUntetheredState(true);
      return;
    }
    untetheredContextRef.current = null;
    writeUntetheredWorkspaceContext(profile.id, null);
    setIsWorkspaceUntetheredState(false);
    const remote = remoteProfileRef.current;
    if (remote) {
      // Snap back to the account-level workspace without a refetch.
      setCurrentUser(remote);
      void refreshWorkspaceOptionsFor(remote);
    }
  }, [refreshWorkspaceOptionsFor]);

  const setSBotAdminModeEnabled = useCallback(
    (enabled: boolean) => {
      const userID = firebaseUser?.uid ?? currentUserRef.current?.id;
      if (!enabled) {
        setIsSBotAdminModeEnabledState(false);
        if (userID) localStorage.setItem(ADMIN_MODE_STORAGE_PREFIX + userID, "false");
        return;
      }
      if (!isSBotLabsAdmin) {
        setIsSBotAdminModeEnabledState(false);
        return;
      }
      setIsSBotAdminModeEnabledState(true);
      if (userID) localStorage.setItem(ADMIN_MODE_STORAGE_PREFIX + userID, "true");
    },
    [firebaseUser, isSBotLabsAdmin],
  );

  const clearAuthError = useCallback(() => setAuthError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      firebaseUser,
      currentUser,
      isAuthenticated: firebaseUser !== null,
      isLoading,
      isResolvingSession,
      isSBotLabsAdmin,
      isSBotAdminModeEnabled,
      workspaceOptions,
      authError,
      profileMaintenanceWarning,
      signIn,
      createAccount,
      signInWithGoogle,
      resetPassword,
      signOut,
      switchActiveWorkspace,
      isWorkspaceUntethered,
      setWorkspaceUntethered,
      setSBotAdminModeEnabled,
      clearAuthError,
      refreshWorkspaceOptions,
    }),
    [
      firebaseUser,
      currentUser,
      isLoading,
      isResolvingSession,
      isSBotLabsAdmin,
      isSBotAdminModeEnabled,
      workspaceOptions,
      authError,
      profileMaintenanceWarning,
      signIn,
      createAccount,
      signInWithGoogle,
      resetPassword,
      signOut,
      switchActiveWorkspace,
      isWorkspaceUntethered,
      setWorkspaceUntethered,
      setSBotAdminModeEnabled,
      clearAuthError,
      refreshWorkspaceOptions,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

export function useCurrentUserNeedsOnboarding(): boolean {
  const { currentUser } = useAuth();
  return currentUser ? needsOnboarding(currentUser) : false;
}
